import React from "react";
import PropTypes from "prop-types";
import {Document, Image, Page, Text, View} from "@react-pdf/renderer";
import * as TimeZonesUtils from "Services/TimeZonesUtils";
import {convertToEst} from "Services/TimeZonesUtils";
import styles from "./AuditReportPDF.styles";
import {AuditItemTypeTitle} from "../../Models/Audit/AuditConstants";

const AuditReportPDF = ({audit, mapImage, canExportNotes}) => {
    const currentDate = new Date();

    function getTitle(index, ant) {
        return `${index + 1} - ${ant.audit_type_item.name} | ${ant.severity_object.name || "N/A"}`;
    }

    const Footer = () => (
        <View style={styles.footer} fixed={true}>
            {audit.can_brand_audit &&
                <>
                    {audit.subscriber_logo &&
                        <View style={styles.subscriberLogoContainer}>
                            <Image
                                src={audit.subscriber_logo}
                                style={styles.subscriberLogo}
                                cache={false}
                            />
                        </View>
                    }
                    <Text style={styles.headline}>{audit.subscriber_name}</Text>
                </>
            }
        </View>
    );

    function renderMedia() {
        return <>
            {renderChunkedItems({
                items: audit.audit_issues,
                title: AuditItemTypeTitle.PINS,
                Nav: () => <ReportNav audit={audit} />,
                Footer: () => <Footer />,
                getTitle: getTitle,
                getDescription: (item) => item.description,
                getNotes: (item) => (canExportNotes ? item.notes : ""),
                getImages: (item) => item.issue_images,
                emptyText: "This pin has no images",
            })}
            {renderChunkedItems({
                items: audit.audit_lines,
                title: AuditItemTypeTitle.LINES,
                Nav: () => <ReportNav audit={audit} />,
                Footer: () => <Footer />,
                getTitle: getTitle,
                getDescription: (item) => item.description,
                getImages: (item) => item.line_images,
                emptyText: "This line has no images",
            })}
            {renderChunkedItems({
                items: audit.audit_areas,
                title: AuditItemTypeTitle.AREAS,
                Nav: () => <ReportNav audit={audit} />,
                Footer: () => <Footer />,
                getTitle: getTitle,
                getDescription: (area) => area.description,
                getImages: (area) => area.area_images,
                emptyText: "This area has no images",
            })}
        </>;
    }

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <ReportNav audit={audit} />
                <Text style={[styles.headline, {textAlign: "center"}]}>
                    Site Vision Pro - Inspection Report
                </Text>
                <Text style={[styles.section, styles.line]}>
                    <Text style={[styles.headline, styles.label]}>Inspection Name: </Text>
                    <Text style={styles.value}>{audit.name.trim()}</Text>
                </Text>
                <Text style={[styles.section, styles.line]}>
                    <Text style={[styles.headline, styles.label]}>Inspection Date: </Text>
                    <Text style={styles.value}>{convertToEst(audit.created_at)}</Text>
                </Text>
                <Text style={[styles.section, styles.line]}>
                    <Text style={[styles.headline, styles.label]}>Address: </Text>
                    <Text style={styles.value}>{audit.location.address.full_address}</Text>
                </Text>
                <Text style={[styles.section, styles.line]}>
                    <Text style={[styles.headline, styles.label]}>Description: </Text>
                    <Text style={styles.value}>
                        {audit.description_type?.name}
                    </Text>
                </Text>
                <Text style={[styles.section, styles.line]}>
                    <Text style={[styles.headline, styles.label]}>Project ID: </Text>
                    <Text style={styles.value}>{audit.project_id}</Text>
                </Text>

                {mapImage && (
                    <View style={styles.section}>
                        <Text style={styles.headline}>Map</Text>
                        <Image src={mapImage} />
                    </View>
                )}
                <Footer />
            </Page>
            {renderMedia()}

            {/* Additional Information */}
            {canExportNotes &&
                <Page size="A4" style={styles.page}>
                    <ReportNav audit={audit} />

                    <View style={styles.table}>
                        <Text style={[styles.titleWithBorder, styles.headline]}>
                            Auditor Additional Information
                        </Text>
                        <View style={{marginVertical: 5}}>
                            <Text>{audit.auditor_additional_notes || "No additional notes"}</Text>
                        </View>
                    </View>

                    <Footer />
                </Page>
            }
            <Page size="A4" style={styles.page}>
                <ReportNav audit={audit} />
                <View style={styles.table}>
                    <Text style={[styles.titleWithBorder, styles.headline]}>
                        Reviewer Additional Information
                    </Text>
                    <View style={{marginVertical: 5}}>
                        <Text>{audit.reviewer_additional_notes || "No additional notes"}</Text>
                    </View>
                </View>
                <View style={styles.table}>
                    <Text style={[styles.titleWithBorder, styles.headline]}>Reviewer Information</Text>
                    <View style={{marginVertical: 5}}>
                        <Text>Name: {audit?.reviewer?.full_name}</Text>
                        <Text>Phone: {audit?.reviewer?.mobile_phone || "N/A"}</Text>
                        <Text>Email: {audit?.reviewer?.email || "N/A"}</Text>
                    </View>
                </View>
                <View style={styles.table}>
                    <Text style={[styles.titleWithBorder, styles.headline]}>Location Information</Text>
                    <View style={{marginVertical: 5}}>
                        <Text>Address: {audit.location.address.full_address || "N/A"}</Text>
                        <Text>Contact: {audit.location.account.name || "N/A"}</Text>
                        <Text>Phone: {audit.location.account.phone || "N/A"}</Text>
                    </View>
                </View>
                <View style={styles.table}>
                    <Text style={[styles.titleWithBorder, styles.headline]}>Report Summary</Text>
                    <View style={{marginVertical: 5}}>
                        <Text>Report Generated: {TimeZonesUtils.convertToEst(currentDate)}</Text>
                    </View>
                </View>
                <Footer />
            </Page>
        </Document>
    );
};

const ReportNav = ({audit}) => {
    return (
        <View style={styles.nav}>
            <View>
                {audit.account.logo && (
                    <View style={styles.navImageContainer}>
                        <Image src={audit.account.logo} style={styles.navImg} />
                    </View>
                )}
            </View>
        </View>
    );
};

function getChunkMediaRowArray(array, chunkSize) {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
}
const renderChunkedItems = ({
    items,
    title,
    Nav,
    Footer,
    getTitle,
    getDescription,
    getNotes = () => "",
    getImages,
    emptyText,
    imagesPerRow = 2,
    maxRowsPerPage = 2,
}) => {
    const chunkArray = (arr, size) => {
        const result = [];
        for (let i = 0; i < arr.length; i += size) {
            result.push(arr.slice(i, i + size));
        }
        return result;
    };

    return items.flatMap((item, index) => {
        const images = getImages(item);
        const chunks = chunkArray(images, imagesPerRow * maxRowsPerPage);
        const hasImages = chunks.length > 0;

        return (hasImages ? chunks : [null]).map((chunk, chunkIndex) =>
            renderTablesInChunks(
                [item],
                title,
                Nav,
                () => (
                    <Table
                        title={getTitle(index, item)}
                        description={getDescription(item)}
                        notes={getNotes(item)}
                        content={
                            chunk ? (
                                <View style={styles.issueImagesContainer} wrap={false}>
                                    {getChunkMediaRowArray(chunk, imagesPerRow).map((row, rowIndex) => (
                                        <View style={styles.imageRow} key={rowIndex} wrap={false}>
                                            {row.map((image, idx) => (
                                                <View style={styles.imageContainer} key={idx}>
                                                    <Image src={image.image_value} style={styles.tableImg} />
                                                    <Text style={styles.noImagesText}>
                                                        {convertToEst(image.created_at)}
                                                    </Text>
                                                    <Text style={styles.noImagesText}>{image.notes}</Text>
                                                </View>
                                            ))}
                                        </View>
                                    ))}
                                </View>
                            ) : (
                                <Text style={styles.noImagesText}>{emptyText}</Text>
                            )
                        }
                    />
                ),
                Footer,
            ));
    });
};


const renderTablesInChunks = (items, title, Nav, renderItem, Footer) => {
    const pages = [];
    for (let i = 0; i < items.length; i += 2) {
        const chunk = items.slice(i, i + 2);
        pages.push(
            <Page size="A4" style={styles.page} key={`${title}-${i}`}>
                <Nav />
                <Text style={styles.headline}>{title}</Text>
                <View style={styles.section}>
                    {chunk.map((item, index) => renderItem(item, i + index))}
                </View>
                <Footer />
            </Page>,
        );
    }
    return pages;
};

const Table = ({title, id, description, notes, content}) => {
    return (
        <View key={id} style={styles.table}>
            <Text style={[styles.titleWithBorder, styles.headline]}>{title}</Text>
            {description && <Text>{description}</Text>}
            {notes && <Text>Notes: {notes}</Text>}
            <View style={{marginVertical: 5}}>
                {content}
            </View>
        </View>
    );
};

AuditReportPDF.propTypes = {
    audit: PropTypes.shape({
        name: PropTypes.string.isRequired,
        subscriber_logo: PropTypes.string,
        can_brand_audit: PropTypes.bool,
        account: PropTypes.shape({
            name: PropTypes.string.isRequired,
            logo: PropTypes.string,
        }).isRequired,
        location: PropTypes.shape({
            address: PropTypes.shape({
                full_address: PropTypes.string,
            }).isRequired,
            account: PropTypes.shape({
                name: PropTypes.string,
                phone: PropTypes.string,
            }).isRequired,
            contact_name: PropTypes.string,
            phone: PropTypes.string,
        }).isRequired,
        reviewer: PropTypes.shape({
            full_name: PropTypes.string,
            mobile_phone: PropTypes.string,
            email: PropTypes.string,
        }).isRequired,
        auditor_additional_notes: PropTypes.string,
        reviewer_additional_notes: PropTypes.string,
        audit_issues: PropTypes.arrayOf(PropTypes.object).isRequired,
        audit_lines: PropTypes.arrayOf(PropTypes.object).isRequired,
        audit_areas: PropTypes.arrayOf(PropTypes.object).isRequired,
    }).isRequired,
    mapImage: PropTypes.string,
    canExportNotes: PropTypes.bool,
};

ReportNav.propTypes = {
    audit: PropTypes.shape({
        name: PropTypes.string.isRequired,
        account: PropTypes.shape({
            name: PropTypes.string.isRequired,
            logo: PropTypes.string,
        }).isRequired,
        location: PropTypes.shape({
            address: PropTypes.shape({
                full_address: PropTypes.string,
            }).isRequired,
        }).isRequired,
    }).isRequired,
};

renderTablesInChunks.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    title: PropTypes.string.isRequired,
    Nav: PropTypes.func.isRequired,
    renderItem: PropTypes.func.isRequired,
    Footer: PropTypes.func.isRequired,
};

Table.propTypes = {
    title: PropTypes.string.isRequired,
    id: PropTypes.string,
    description: PropTypes.string,
    notes: PropTypes.string,
    content: PropTypes.node,
};

export default AuditReportPDF;
